import type { CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { Grid, GridProps, Skeleton } from "@mui/material";
import { Theme } from "@mui/material/styles";
import {
  getPdfFriendlyCardStyles,
  getSubCardStyles
} from "@themes/compositions/glass";
import { ElementType } from "react";

export type GridCardSurface = "glass" | "exportSafe" | "inset";

type GridCardExtraProps = {
  clickable: boolean;
  surface?: GridCardSurface;
};

export const getSurfaceStyles = (theme: Theme, surface: GridCardSurface) => {
  switch (surface) {
    case "exportSafe": // use it for pdf screenshots
      return getPdfFriendlyCardStyles(theme);
    case "inset": // drawer, form section
      return {
        backgroundColor: theme.vars.palette.background.default,
        border: `1px solid ${theme.vars.palette.border.hairline}`,
        borderRadius: theme.shape.borderRadiusMd
      };
    case "glass":
    default:
      return getSubCardStyles(theme);
  }
};

const BaseGridCard = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "clickable" && prop !== "surface"
})<GridCardExtraProps>(
  ({ clickable, surface, theme }) =>
    ({
      cursor: clickable ? "pointer" : "default",

      transition: theme.transitions.create(["transform", "box-shadow"], {
        duration: theme.transitions.duration.shorter
      }),

      ...getSurfaceStyles(theme, surface ?? "glass"),

      ...(clickable && {
        "&:hover": {
          boxShadow: theme.vars.appEffects.shadow.md,
          transform: "scale(1.01)"
        }
      })
    }) as CSSObject
) as unknown as ElementType<GridProps & GridCardExtraProps>;

export interface GridCardProps extends GridProps {
  onClick?: () => void;
  padding?: number | string;
  ref?: React.RefObject<HTMLDivElement>;
  surface?: GridCardSurface;
  loading?: boolean;
  skeletonHeight?: number;
}

export const GridCard = ({
  children,
  id,
  loading,
  onClick,
  padding,
  ref,
  skeletonHeight = 200,
  surface = "glass",
  sx,
  ...props
}: GridCardProps) => {
  return (
    <>
      {loading ? (
        <Grid {...props}>
          <Skeleton
            variant="rounded"
            height={skeletonHeight}
            sx={{ width: "100%" }}
          />
        </Grid>
      ) : (
        <BaseGridCard
          container
          clickable={!!onClick}
          surface={surface}
          id={id}
          onClick={onClick}
          ref={ref}
          sx={[
            { gap: 2, px: 2, py: padding ?? 2, width: "100%" },
            ...(Array.isArray(sx) ? sx : sx != null ? [sx] : [])
          ]}
          {...props}
        >
          {children}
        </BaseGridCard>
      )}
    </>
  );
};
